import {useEffect, useState} from "react";
import {App as AntdApp, Alert, Empty, Table, Tabs, Tag, Tooltip} from "antd";
import {Button, ExportButtons, FontAwesomeIcon, Loading} from "~/components";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {useGetBacklinkReportQuery, useSyncBacklinksMutation} from "~/reduxs/api/backlinkApiSlice";

const nf = (v) => Number(v || 0).toLocaleString('vi-VN');
const dash = <span style={{color: '#9ca3af'}}>—</span>;

/** Thẻ chỉ số tổng quan. */
function Stat({label, value, sub, color = '#111827'}) {
	return (
		<div className="app-card" style={{padding: '14px 16px', minWidth: 150, flex: '1 1 150px'}}>
			<div style={{fontSize: 12, color: '#6b7280', marginBottom: 4}}>{label}</div>
			<div style={{fontSize: 22, fontWeight: 700, color}}>{value}</div>
			{sub && <div style={{fontSize: 12, color: '#9ca3af', marginTop: 2}}>{sub}</div>}
		</div>
	);
}

/**
 * Báo cáo Backlink của domain dự án (từ cache BE, TTL 7 ngày): thẻ tổng quan (total/ref
 * domains/dofollow-nofollow/rank/spam) + 4 tab (Backlinks / Domain giới thiệu / Anchor /
 * Mới-Mất theo tháng). Đồng bộ chạy nền — poll 8s khi syncing.
 */
function BacklinkReport({projectId}) {

	const {notification} = AntdApp.useApp();
	const [pollOn, setPollOn] = useState(false);

	const {data, isLoading} = useGetBacklinkReportQuery(projectId, {pollingInterval: pollOn ? 8000 : 0});
	const [syncBacklinks, {isLoading: requesting}] = useSyncBacklinksMutation();

	// Poll khi BE báo đang đồng bộ; dừng khi xong (data mới tự thay).
	useEffect(() => { setPollOn(!!data?.syncing); }, [data?.syncing]);

	if (isLoading) return <Loading />;
	if (!data) return null;

	const quota = data.quota || {};

	// Gói không có lượt backlink (và không phải root) → panel khoá.
	if (quota.can === false) {
		return (
			<Alert type="warning" showIcon style={{marginTop: 8}}
				message="Gói của bạn chưa hỗ trợ phân tích Backlink"
				description={<>
					Phân tích backlink (tổng số, domain giới thiệu, anchor, dofollow/nofollow, mới/mất) có ở gói Pro trở lên.
					<div style={{marginTop: 10}}>
						<Button primary small onClick={() => (window.location.href = '/subscription')}
							leftIcon={<FontAwesomeIcon icon="fa-light fa-arrow-up-right-dots" />}>Xem các gói</Button>
					</div>
				</>} />
		);
	}

	const doSync = async () => {
		try {
			await syncBacklinks(projectId).unwrap();
			notification.success({message: 'Đã yêu cầu đồng bộ', description: 'Dữ liệu backlink cập nhật sau ít phút.'});
			setPollOn(true);
		} catch (e) {
			notification.error({message: 'Không đồng bộ được', description: rtkErrorMessage(e, 'Vui lòng thử lại.')});
		}
	};

	const s = data.summary;

	const syncBar = (
		<div style={{display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14}}>
			<Button primary small loading={requesting || data.syncing} onClick={doSync}
				leftIcon={<FontAwesomeIcon icon="fa-light fa-rotate" />}>
				{data.syncing ? 'Đang đồng bộ…' : 'Đồng bộ backlink'}
			</Button>
			{typeof quota.remaining === 'number' && (
				<span style={{fontSize: 12, color: '#6b7280'}}>
					Lượt đồng bộ: {quota.used}/{quota.limit} tháng ({quota.remaining} còn lại) · dữ liệu giữ {quota.ttl_days} ngày
				</span>
			)}
			{data.fetched_at && (
				<span style={{fontSize: 12, color: '#9ca3af'}}>
					Đồng bộ gần nhất: {data.fetched_at}{data.fresh ? '' : ' (đã cũ — nên đồng bộ lại)'}
				</span>
			)}
		</div>
	);

	if (!data.has_data) {
		return (
			<div>
				{quota.sandbox && (
					<Alert type="warning" showIcon style={{marginBottom: 14}}
						message="Đang dùng dữ liệu mẫu (chế độ thử nghiệm) — số liệu backlink là dữ liệu demo." />
				)}
				{syncBar}
				<Empty description={
					data.syncing
						? 'Đang đồng bộ backlink lần đầu — chờ ít phút, trang tự cập nhật.'
						: `Chưa có dữ liệu backlink cho ${data.domain}. Bấm "Đồng bộ backlink" để lấy dữ liệu (tốn 1 lượt của gói).`
				} />
			</div>
		);
	}

	const backlinkRows = data.backlinks?.rows || [];
	const refRows = data.referring_domains?.rows || [];
	const anchorRows = data.anchors?.rows || [];
	const newLostRows = data.new_lost?.rows || [];

	const backlinkCols = [
		{title: 'Trang nguồn', key: 'from', ellipsis: true,
			render: (_, r) => (
				<span>
					<a href={r.url_from} target="_blank" rel="noreferrer">{r.title || r.url_from}</a>
					<div style={{fontSize: 12, color: '#9ca3af'}}>{r.domain_from}</div>
				</span>
			)},
		{title: 'Anchor', dataIndex: 'anchor', key: 'anchor', width: 200, ellipsis: true, responsive: ['md'],
			render: (v, r) => v || <Tag>{r.item_type === 'image' ? 'ảnh' : 'không anchor'}</Tag>},
		{title: 'Trang đích', dataIndex: 'url_to', key: 'url_to', width: 220, ellipsis: true, responsive: ['lg'],
			render: (v) => <span style={{fontSize: 12, color: '#6b7280'}}>{v}</span>},
		{title: 'Loại', dataIndex: 'dofollow', key: 'dofollow', width: 100, align: 'center',
			filters: [{text: 'Dofollow', value: true}, {text: 'Nofollow', value: false}],
			onFilter: (v, r) => r.dofollow === v,
			render: (v) => <Tag color={v ? 'green' : 'default'}>{v ? 'dofollow' : 'nofollow'}</Tag>},
		{title: 'Rank', dataIndex: 'rank', key: 'rank', width: 80, align: 'center', responsive: ['md'],
			sorter: (a, b) => a.rank - b.rank},
		{title: 'Phát hiện', dataIndex: 'first_seen', key: 'first_seen', width: 110, align: 'center', responsive: ['lg']},
	];

	const refCols = [
		{title: 'Domain', dataIndex: 'domain', key: 'domain', ellipsis: true},
		{title: 'Rank', dataIndex: 'rank', key: 'rank', width: 90, align: 'center', sorter: (a, b) => a.rank - b.rank},
		{title: 'Backlinks', dataIndex: 'backlinks', key: 'backlinks', width: 120, align: 'center',
			sorter: (a, b) => a.backlinks - b.backlinks, defaultSortOrder: 'descend', render: nf},
		{title: 'Trang giới thiệu', dataIndex: 'referring_pages', key: 'referring_pages', width: 140, align: 'center', responsive: ['md'], render: nf},
		{title: 'Phát hiện', dataIndex: 'first_seen', key: 'first_seen', width: 110, align: 'center', responsive: ['lg']},
	];

	const anchorCols = [
		{title: 'Anchor text', dataIndex: 'anchor', key: 'anchor', ellipsis: true,
			render: (v) => v || <Tag>không anchor / ảnh</Tag>},
		{title: 'Backlinks', dataIndex: 'backlinks', key: 'backlinks', width: 130, align: 'center',
			sorter: (a, b) => a.backlinks - b.backlinks, defaultSortOrder: 'descend', render: nf},
		{title: 'Domain giới thiệu', dataIndex: 'referring_domains', key: 'referring_domains', width: 160, align: 'center', render: nf},
	];

	const newLostCols = [
		{title: 'Tháng', dataIndex: 'date', key: 'date', width: 110},
		{title: 'Backlink mới', dataIndex: 'new_backlinks', key: 'nb', align: 'center',
			render: (v) => <span style={{color: '#16a34a', fontWeight: 600}}>+{nf(v)}</span>},
		{title: 'Backlink mất', dataIndex: 'lost_backlinks', key: 'lb', align: 'center',
			render: (v) => <span style={{color: '#dc2626', fontWeight: 600}}>-{nf(v)}</span>},
		{title: 'Domain mới', dataIndex: 'new_referring_domains', key: 'nd', align: 'center', responsive: ['md'],
			render: (v) => <span style={{color: '#16a34a'}}>+{nf(v)}</span>},
		{title: 'Domain mất', dataIndex: 'lost_referring_domains', key: 'ld', align: 'center', responsive: ['md'],
			render: (v) => <span style={{color: '#dc2626'}}>-{nf(v)}</span>},
	];

	const buildExcel = () => [
		...(s ? [{name: 'Tổng quan', aoa: [
			['Chỉ số', 'Giá trị'],
			['Domain', data.domain], ['Tổng backlink', s.backlinks], ['Domain giới thiệu', s.referring_domains],
			['Dofollow', s.dofollow], ['Nofollow', s.nofollow], ['IP giới thiệu', s.referring_ips],
			['Backlink hỏng', s.broken_backlinks], ['Domain rank', s.rank], ['Spam score', s.spam_score],
		]}] : []),
		{name: 'Backlinks', aoa: [['Domain nguồn', 'URL nguồn', 'Anchor', 'Loại', 'Rank', 'Phát hiện'],
			...backlinkRows.map((r) => [r.domain_from, r.url_from, r.anchor, r.dofollow ? 'dofollow' : 'nofollow', r.rank, r.first_seen])]},
		{name: 'Domain giới thiệu', aoa: [['Domain', 'Rank', 'Backlinks', 'Trang giới thiệu', 'Phát hiện'],
			...refRows.map((r) => [r.domain, r.rank, r.backlinks, r.referring_pages, r.first_seen])]},
		{name: 'Anchor', aoa: [['Anchor', 'Backlinks', 'Domain giới thiệu'],
			...anchorRows.map((r) => [r.anchor, r.backlinks, r.referring_domains])]},
		{name: 'Mới - Mất', aoa: [['Tháng', 'Backlink mới', 'Backlink mất', 'Domain mới', 'Domain mất'],
			...newLostRows.map((r) => [r.date, r.new_backlinks, r.lost_backlinks, r.new_referring_domains, r.lost_referring_domains])]},
	];

	const buildPdf = () => ({
		title: `Backlink: ${data.domain}`,
		subtitle: `Đồng bộ ${data.fetched_at || ''}`,
		sections: [
			{columns: ['Domain nguồn', 'Anchor', 'Loại', 'Rank'],
				rows: backlinkRows.slice(0, 50).map((r) => [r.domain_from, r.anchor || '', r.dofollow ? 'dofollow' : 'nofollow', r.rank])},
			{columns: ['Domain giới thiệu', 'Rank', 'Backlinks'],
				rows: refRows.slice(0, 50).map((r) => [r.domain, r.rank, r.backlinks])},
		],
	});

	const tabs = [
		{key: 'backlinks', label: `Backlinks (${nf(data.backlinks?.total || backlinkRows.length)})`,
			children: <Table rowKey={(r) => r.url_from + r.url_to} size="small" columns={backlinkCols}
				dataSource={backlinkRows} scroll={{x: 'max-content'}} pagination={{pageSize: 20, hideOnSinglePage: true, showSizeChanger: false}} />},
		{key: 'domains', label: `Domain giới thiệu (${nf(data.referring_domains?.total || refRows.length)})`,
			children: <Table rowKey="domain" size="small" columns={refCols}
				dataSource={refRows} scroll={{x: 'max-content'}} pagination={{pageSize: 20, hideOnSinglePage: true, showSizeChanger: false}} />},
		{key: 'anchors', label: `Anchor (${nf(data.anchors?.total || anchorRows.length)})`,
			children: <Table rowKey={(r, i) => r.anchor + i} size="small" columns={anchorCols}
				dataSource={anchorRows} scroll={{x: 'max-content'}} pagination={{pageSize: 20, hideOnSinglePage: true, showSizeChanger: false}} />},
		{key: 'newlost', label: 'Mới / Mất',
			children: <Table rowKey="date" size="small" columns={newLostCols}
				dataSource={newLostRows} scroll={{x: 'max-content'}} pagination={false} />},
	];

	const total = (s?.dofollow || 0) + (s?.nofollow || 0);
	const dfPct = total > 0 ? Math.round(((s?.dofollow || 0) / total) * 100) : 0;

	return (
		<div>
			{quota.sandbox && (
				<Alert type="warning" showIcon style={{marginBottom: 14}}
					message="Đang dùng dữ liệu mẫu (chế độ thử nghiệm) — số liệu backlink là dữ liệu demo, không phải của domain bạn." />
			)}

			{syncBar}

			{s && (
				<div style={{display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16}}>
					<Stat label="Tổng backlink" value={nf(s.backlinks)} sub={`${nf(s.referring_pages)} trang giới thiệu`} />
					<Stat label="Domain giới thiệu" value={nf(s.referring_domains)} sub={`${nf(s.referring_ips)} IP`} />
					<Stat label="Dofollow / Nofollow" value={`${dfPct}%`} sub={`${nf(s.dofollow)} dofollow · ${nf(s.nofollow)} nofollow`} color="#16a34a" />
					<Stat label={<Tooltip title="Điểm uy tín domain (kiểu Domain Authority)">Domain rank</Tooltip>}
						value={s.rank || dash} sub={s.spam_score ? `Spam score ${s.spam_score}` : null}
						color={s.rank >= 300 ? '#16a34a' : '#111827'} />
					<Stat label="Backlink hỏng" value={nf(s.broken_backlinks)} color={s.broken_backlinks > 0 ? '#dc2626' : '#111827'} />
				</div>
			)}

			<div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: 8}}>
				<ExportButtons filename={`backlink-${projectId}`} buildExcel={buildExcel} buildPdf={buildPdf} />
			</div>

			<Tabs items={tabs} />
		</div>
	);
}

export default BacklinkReport;
